import { store } from '@/app/store'
import { COOKIE_SESSION } from '@/constants/Common'
import { login as loginApi, signup as signupApi } from '@/services'
import { LoginPayload, SignupPayload } from '@/types'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { updateUser, getProfile } from '../user/userSlice'

interface AuthStore {
  token: string
  refresh: string
  isLoading: boolean
  // A session can be established (cookie set) yet still owe a strong second factor before it is
  // allowed to do anything else — the post-2FA STRONG_MFA_SETUP enforcement. While this is true the
  // account is NOT fully authenticated: the app must show only the enrolment gate and fire no other
  // calls or background state. Set from the 2FA-verify 200 body (login) or by the enforcement 428
  // interceptor; cleared when the user finishes enrolment, when a profile fetch succeeds
  // (requirement satisfied) or on logout. NOT persisted (see persistReducer): it is a fact the
  // server stated about THIS session, and a stale true would trap the user in the gate.
  strongMfaSetupRequired: boolean
}

export const LoginPrefix = '@Auth/Login'
export const SignupPrefix = '@Auth/Signup'
export const LogoutPrefix = '@Auth/Logout'
export const RefreshPrefix = '@Auth/Refresh'

export const login = createAsyncThunk(LoginPrefix, async (payload: LoginPayload, { rejectWithValue }) => {
  try {
    const res = await loginApi(payload)
    await store.dispatch(updateUser(res.result.user))
    return res
  } catch (err: any) {
    // Carry the STATUS alongside the body. 428 means "we emailed you a sign-in link" and 401
    // means "your password is wrong"; the bodies are identical, so dropping the status here
    // left the login screen unable to tell them apart.
    return rejectWithValue({ ...err.response?.data, status: err.response?.status })
  }
})

export const signup = createAsyncThunk(SignupPrefix, async (payload: SignupPayload, { rejectWithValue }) => {
  try {
    const res = await signupApi(payload)
    // Dispatch from the thunk, not the fulfilled reducer: a reducer may not dispatch (Redux
    // error #9). login above does the same.
    if (res.result.user) await store.dispatch(updateUser(res.result.user))
    return res
  } catch (err: any) {
    // No response at all (network error, gateway timeout without CORS): the account may still have
    // been created, so say so plainly rather than crashing on `err.response`. The status rides
    // along so the page can tell "already created" (409) from other refusals.
    if (!err?.response) {
      return rejectWithValue({ message: "We couldn't reach DUPR. Please try again.", status: 0 })
    }
    return rejectWithValue({ ...err.response.data, status: err.response.status })
  }
})

const initialState: AuthStore = {
  token: '',
  refresh: '',
  isLoading: false,
  strongMfaSetupRequired: false,
}

const authSlice = createSlice({
  name: 'AUTH_SLICE',
  initialState,
  reducers: {
    resetAuth: (state) => {
      Object.assign(state, initialState)
    },
    setToken: (state, action) => {
      state.token = action.payload
    },
    setRefresh: (state, action) => {
      state.refresh = action.payload
    },
    setStrongMfaSetupRequired: (state, action) => {
      state.strongMfaSetupRequired = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(login.fulfilled, (state) => {
      // First-party sessions ride HttpOnly cookies now; the body carries no token. Record the
      // marker so the app knows a session exists. (Login itself answers 428 → 2FA, so this only
      // fires on a path that already established the cookie.)
      state.token = COOKIE_SESSION
      state.refresh = COOKIE_SESSION
      state.isLoading = false
    })
    builder.addCase(login.rejected, (state) => {
      state.isLoading = false
    })

    builder.addCase(signup.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(signup.fulfilled, (state) => {
      // Signup establishes the session in HttpOnly cookies; no token in the body. The user is
      // dispatched from the thunk (a reducer may not dispatch — Redux error #9).
      state.token = COOKIE_SESSION
      state.refresh = COOKIE_SESSION
      state.isLoading = false
    })
    builder.addCase(signup.rejected, (state) => {
      state.isLoading = false
    })
    // A profile fetch that SUCCEEDS proves the session owes no strong-MFA setup (the enforcement
    // filter would have 428'd it otherwise), so it is the authoritative "requirement cleared"
    // signal — it heals a stale persisted flag after the user enrols, and keeps a normal session
    // ungated. The flag is only ever SET by the enforcement 428 interceptor.
    builder.addCase(getProfile.fulfilled, (state) => {
      state.strongMfaSetupRequired = false
    })
  },
})

const { reducer, actions } = authSlice

export const { resetAuth, setToken, setRefresh, setStrongMfaSetupRequired } = actions

export default reducer
